import { cva, cx, VariantProps } from 'class-variance-authority';
import { DetailedHTMLProps, TextareaHTMLAttributes } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';

interface DeprecatedProps {
    /** @deprecated */
    maxRows?: number;
    /** @deprecated */
    minRows?: number;
    /** @deprecated */
    cacheMeasurements?: boolean;
    /** @deprecated */
    css?: any;
    /** @deprecated */
    helperColor?: string;
    /** @deprecated */
    status?: string;
    /** @deprecated */
    onHeightChange?: (height: number) => void;
}

const textareaClassNames = cva(
    'tw-p-2 tw-border-accents-0 tw-border-solid tw-bg-accents-0 tw-rounded-xl tw-transition-colors tw-outline-none tw-max-h-36',
    {
        variants: {
            fullWidth: { true: 'tw-w-full' },
            isInvalid: {
                true: 'tw-bg-errorLight focus:tw-border-error active:tw-border-error',
                false: 'tw-bg-accents-0 focus:tw-border-success active:tw-border-success'
            }
        },
        defaultVariants: {
            fullWidth: true,
            isInvalid: false
        }
    }
);

const labelClassNames = cva('tw-block tw-font-normal tw-text-sm tw-mb-1 tw-pl-1', {
    variants: {
        isInvalid: {
            true: 'tw-text-error',
            false: 'tw-text-accents-9'
        }
    },
    defaultVariants: {
        isInvalid: false
    }
});

const containerClassNames = cva('tw-mb-4', {
    variants: {
        fullWidth: {
            true: 'tw-w-full',
            false: ''
        }
    },
    defaultVariants: {
        fullWidth: false
    }
});

interface Props
    extends
        VariantProps<typeof textareaClassNames>,
        DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
        DeprecatedProps {
    name: string;
    options?: RegisterOptions;
    withoutShifting?: boolean;
    label?: string | React.JSX.Element;
    helperText?: string;
    containerClassName?: string;
    isInvalid?: boolean;
    onHeightChange?: (height: number) => void;
}

const FormTextarea = ({
    name,
    label,
    onBlur,
    fullWidth,
    onChange,
    isInvalid,
    helperText,
    containerClassName,
    options,
    rows,

    status,
    maxRows,
    minRows,
    cacheMeasurements,
    css,
    helperColor,
    ...props
}: Props) => {
    const {
        register,
        formState: { errors }
    } = useFormContext();
    const errorMessage = errors?.[name]?.message;
    return (
        <div className={cx(containerClassNames({ fullWidth }), containerClassName)}>
            {label && (
                <label htmlFor={'input_' + name} className={labelClassNames({ isInvalid })}>
                    {label}
                </label>
            )}
            <textarea
                {...props}
                id={'input_' + name}
                className={textareaClassNames({ isInvalid, fullWidth })}
                {...register(name, { onBlur, onChange, ...options })}
            />
            {helperText && (
                <span
                    className={cx(isInvalid ? 'tw-text-error' : 'tw-text-accents-6', 'tw-ml-2 tw-block tw-text-[10px]')}
                >
                    {(errorMessage || helperText) as string}
                </span>
            )}
        </div>
    );
};

export default FormTextarea;
